import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';
import {
  Activity,
  Trophy,
  Video,
  Cpu,
  Users,
  BarChart3,
  ChevronRight,
  CheckCircle,
  Globe,
  Zap,
  ArrowRight,
  Target,
} from 'lucide-react';

/* ──────────────────────────────────────────────
   Brand tokens (same as VukzHomePage)
   ────────────────────────────────────────────── */
const V = {
  bg: '#ffffff',
  text: '#111111',
  textSoft: '#555555',
  textMute: '#8a8a8a',
  border: '#e6e6e6',
  cardBg: '#fafafa',
  tagBg: '#f3f3f3',
};

/* ─── Products ──────────────────────────────── */
const PRODUCTS = [
  {
    icon: Activity,
    title: 'Performance OS',
    subtitle: 'Training, Periodization, Progress',
    desc: 'Training operating system with role-based access. Plan sessions, monitor training load and track athlete development across sports.',
    tags: ['RBAC', 'Multi-sport', 'API'],
  },
  {
    icon: Trophy,
    title: 'Rankings & Results',
    subtitle: 'Brazil, USA, Australia',
    desc: 'Global performance benchmarks in swimming with structured data from official competitions — updated in real time.',
    tags: ['Swimming', 'Global', 'Live data'],
  },
  {
    icon: Cpu,
    title: 'VUKZ Mind',
    subtitle: 'AI Analytics & Insights',
    desc: 'Predictive sports performance analysis with automatic pattern detection, trend identification and AI-generated reports.',
    tags: ['AI Agents', 'Analytics', 'Insights'],
  },
  {
    icon: Video,
    title: 'SwimVision',
    subtitle: 'Computer Vision + Digital Twin',
    desc: 'Multi-camera biomechanical analysis with 3D reconstruction, pose estimation and athlete digital twin technology.',
    tags: ['Computer Vision', 'Digital Twin', 'SMPL', 'Beta'],
  },
];

/* ─── What VUKZ connects ─────────────────────── */
const CONNECTIONS = [
  { icon: BarChart3, label: 'Performance Analytics' },
  { icon: Video,     label: 'Computer Vision' },
  { icon: Cpu,       label: 'AI Agents' },
  { icon: Trophy,    label: 'Competition Data' },
  { icon: Activity,  label: 'Training Loads' },
  { icon: Users,     label: 'Team Workspaces (RBAC)' },
  { icon: Globe,     label: 'Global Rankings' },
  { icon: Target,    label: 'Goal Tracking' },
];

/* ─── Built for ──────────────────────────────── */
const BUILT_FOR = [
  'Clubs & Academies',
  'Sports Federations',
  'Head Coaches',
  'Strength & Conditioning Coaches',
  'Athletes',
  'Performance Teams',
  'Talent Scouts / Recruiters',
  'Sports Nutritionists',
];

/* ─── Why Azure ──────────────────────────────── */
const AZURE_BULLETS = [
  'Available via Azure Marketplace — single sign-on with your Microsoft account',
  'Hosted on Microsoft Azure Brazil South and East US regions',
  'Compliant with LGPD (Brazil) and GDPR (Europe)',
  'ISO 27001-aligned data governance powered by Microsoft Purview',
  'Row-Level Security ensures tenant data isolation',
  'SOC 2-oriented architecture with audit trails and RBAC',
];

/* ──────────────────────────────────────────────────────────────────────────── */
/*  Helper components                                                           */
/* ──────────────────────────────────────────────────────────────────────────── */
const Tag = ({ label }) => (
  <span
    className="text-xs font-medium px-2 py-0.5 rounded"
    style={{ backgroundColor: V.tagBg, color: V.textMute }}
  >
    {label}
  </span>
);

const ProductCard = ({ icon: Icon, title, subtitle, desc, tags }) => (
  <div
    className="rounded-2xl border p-6 flex flex-col gap-3 hover:shadow-md transition-shadow"
    style={{ backgroundColor: V.cardBg, borderColor: V.border }}
  >
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: '#111', color: '#fff' }}
      >
        <Icon size={20} />
      </div>
      <div>
        <p className="font-bold text-sm" style={{ color: V.text }}>{title}</p>
        <p className="text-xs" style={{ color: V.textMute }}>{subtitle}</p>
      </div>
    </div>
    <p className="text-sm leading-relaxed" style={{ color: V.textSoft }}>{desc}</p>
    <div className="flex flex-wrap gap-1.5 pt-1">
      {tags.map(t => <Tag key={t} label={t} />)}
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────────── */
/*  Page                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */
const EnSportsLandingPage = () => (
  <div style={{ backgroundColor: V.bg, color: V.text, fontFamily: 'system-ui, sans-serif' }}>
    <SEOHead
      title="VUKZ Sports Intelligence — AI-powered Performance OS for sports"
      description="VUKZ Sports Intelligence OS helps clubs, federations, coaches and performance teams turn sports data, computer vision and AI agents into an integrated operating system."
      lang="en"
    />

    {/* ── Nav bar ─────────────────────────────────────────── */}
    <header
      className="sticky top-0 z-50 flex items-center justify-between px-6 py-4 border-b"
      style={{ backgroundColor: V.bg, borderColor: V.border }}
    >
      <Link to="/" className="flex items-center gap-2">
        <img src="/logo-vukz.png" alt="VUKZ" className="h-8 w-8 object-contain" onError={e => { e.target.style.display='none'; }} />
        <span className="text-lg font-black tracking-tight" style={{ color: V.text }}>VUKZ</span>
      </Link>
      <nav className="hidden md:flex items-center gap-6 text-sm" style={{ color: V.textSoft }}>
        <a href="#products" className="hover:text-black transition-colors">Products</a>
        <a href="#built-for" className="hover:text-black transition-colors">Built for</a>
        <a href="#azure" className="hover:text-black transition-colors">Azure Marketplace</a>
        <Link to="/en/governance-intelligence" className="hover:text-black transition-colors">Governance OS</Link>
      </nav>
      <div className="flex items-center gap-3">
        <Link to="/login" className="text-sm font-medium" style={{ color: V.textSoft }}>Sign in</Link>
        <Link
          to="/pre-cadastro"
          className="text-sm font-bold px-4 py-2 rounded-xl transition-colors"
          style={{ backgroundColor: '#111', color: '#fff' }}
        >
          Request access
        </Link>
      </div>
    </header>

    {/* ── Hero ────────────────────────────────────────────── */}
    <section className="max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
      <div
        className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border mb-6"
        style={{ borderColor: V.border, color: V.textMute }}
      >
        <Zap size={12} />
        Available on Azure Marketplace
      </div>

      <h1 className="text-5xl font-black leading-tight tracking-tight mb-4">
        AI-powered Sports Intelligence<br />
        <span style={{ color: V.textSoft }}>and Performance OS</span>
      </h1>

      <p className="text-lg max-w-2xl mx-auto mb-10" style={{ color: V.textSoft }}>
        VUKZ Sports Intelligence helps clubs, federations, coaches and performance teams
        turn <strong>sports data, computer vision and AI agents</strong> into an integrated
        operating system.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/pre-cadastro"
          className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl transition-colors"
          style={{ backgroundColor: '#111', color: '#fff' }}
        >
          Request access <ArrowRight size={16} />
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-xl border transition-colors hover:bg-gray-50"
          style={{ borderColor: V.border, color: V.text }}
        >
          Explore platform <ChevronRight size={16} />
        </Link>
      </div>
    </section>

    {/* ── Products ────────────────────────────────────────── */}
    <section id="products" className="max-w-5xl mx-auto px-6 py-16">
      <h2 className="text-3xl font-black mb-2">Products</h2>
      <p className="mb-10" style={{ color: V.textSoft }}>
        Four modular products. One integrated Sports Intelligence platform.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {PRODUCTS.map(p => <ProductCard key={p.title} {...p} />)}
      </div>
    </section>

    {/* ── What VUKZ connects ──────────────────────────────── */}
    <section
      className="py-16"
      style={{ backgroundColor: V.cardBg, borderTop: `1px solid ${V.border}`, borderBottom: `1px solid ${V.border}` }}
    >
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-3xl font-black mb-2">What VUKZ Sports connects</h2>
        <p className="mb-10" style={{ color: V.textSoft }}>
          All the layers of a modern high-performance sports operation — in one system.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {CONNECTIONS.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl border text-center"
              style={{ backgroundColor: V.bg, borderColor: V.border }}
            >
              <Icon size={22} style={{ color: V.text }} />
              <span className="text-xs font-semibold" style={{ color: V.textSoft }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Built for ───────────────────────────────────────── */}
    <section id="built-for" className="max-w-5xl mx-auto px-6 py-16">
      <h2 className="text-3xl font-black mb-2">Built for</h2>
      <p className="mb-10" style={{ color: V.textSoft }}>
        Role-based access (RBAC) ensures every stakeholder sees only what they need.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {BUILT_FOR.map(label => (
          <div
            key={label}
            className="flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium"
            style={{ borderColor: V.border, color: V.text }}
          >
            <CheckCircle size={14} style={{ color: '#111', flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>
    </section>

    {/* ── Azure Marketplace ───────────────────────────────── */}
    <section
      id="azure"
      className="py-16"
      style={{ backgroundColor: '#f7f9ff', borderTop: `1px solid ${V.border}` }}
    >
      <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row gap-12 items-start">
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#0078d4' }}>
            Microsoft Azure
          </div>
          <h2 className="text-3xl font-black mb-4">Cloud-ready. Marketplace-ready.</h2>
          <p className="mb-8" style={{ color: V.textSoft }}>
            VUKZ Sports Intelligence is a SaaS product available through the Azure Marketplace.
            Secure, scalable and modular — designed for sports organizations of any size.
          </p>
          <ul className="flex flex-col gap-3">
            {AZURE_BULLETS.map(b => (
              <li key={b} className="flex items-start gap-2 text-sm" style={{ color: V.textSoft }}>
                <CheckCircle size={15} style={{ color: '#0078d4', flexShrink: 0, marginTop: 2 }} />
                {b}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex-1 flex flex-col gap-4">
          <div
            className="rounded-2xl border p-6"
            style={{ backgroundColor: V.bg, borderColor: V.border }}
          >
            <p className="text-sm font-bold mb-1" style={{ color: V.text }}>Sports Module</p>
            <p className="text-3xl font-black mb-1">From US$ 99<span className="text-base font-normal" style={{ color: V.textMute }}>/mo</span></p>
            <p className="text-xs mb-4" style={{ color: V.textMute }}>Per module · billed monthly</p>
            <Link
              to="/marketplace/activate"
              className="flex items-center justify-center gap-2 w-full font-bold py-3 rounded-xl text-sm"
              style={{ backgroundColor: '#111', color: '#fff' }}
            >
              Get started on Azure Marketplace <ArrowRight size={14} />
            </Link>
          </div>
          <div
            className="rounded-2xl border p-6"
            style={{ backgroundColor: V.bg, borderColor: V.border }}
          >
            <p className="text-sm font-bold mb-1" style={{ color: V.text }}>Sports Complete Suite</p>
            <p className="text-3xl font-black mb-1">From US$ 1,500<span className="text-base font-normal" style={{ color: V.textMute }}>/mo</span></p>
            <p className="text-xs mb-4" style={{ color: V.textMute }}>All modules · priority support</p>
            <Link
              to="/contato"
              className="flex items-center justify-center gap-2 w-full font-semibold py-3 rounded-xl text-sm border"
              style={{ borderColor: V.border, color: V.text }}
            >
              Contact us <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ── CTA band ────────────────────────────────────────── */}
    <section className="max-w-5xl mx-auto px-6 py-20 text-center">
      <h2 className="text-4xl font-black mb-4">
        Ready to build your Sports Intelligence OS?
      </h2>
      <p className="text-lg mb-10 max-w-xl mx-auto" style={{ color: V.textSoft }}>
        Join clubs, federations and performance teams already running on VUKZ.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/pre-cadastro"
          className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl"
          style={{ backgroundColor: '#111', color: '#fff' }}
        >
          Request access <ArrowRight size={16} />
        </Link>
        <Link
          to="/en/governance-intelligence"
          className="inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-xl border hover:bg-gray-50 transition-colors"
          style={{ borderColor: V.border, color: V.text }}
        >
          Explore Governance OS <ChevronRight size={16} />
        </Link>
      </div>
    </section>

    {/* ── Footer ──────────────────────────────────────────── */}
    <footer
      className="border-t px-6 py-10 text-center text-xs"
      style={{ borderColor: V.border, color: V.textMute }}
    >
      <p className="mb-2 font-bold text-sm" style={{ color: V.text }}>VUKZ Sports Intelligence</p>
      <p className="mb-4">Arena Stats Ltda. · CNPJ 61.698.906/0001-16 (Brazil)</p>
      <div className="flex justify-center gap-6 flex-wrap">
        <Link to="/en/privacy" className="hover:text-black transition-colors">Privacy Policy</Link>
        <Link to="/en/terms" className="hover:text-black transition-colors">Terms of Service</Link>
        <Link to="/en/sla" className="hover:text-black transition-colors">SLA</Link>
        <Link to="/contato" className="hover:text-black transition-colors">Contact</Link>
        <Link to="/pt" className="hover:text-black transition-colors">Português</Link>
      </div>
    </footer>
  </div>
);

export default EnSportsLandingPage;
